export const BULK_BATCH_LIMIT = 50
export const BULK_LEADS_PATH = "leads/bulk/"

export const BULK_ACTION_ARCHIVE = "archive"
export const BULK_ACTION_UNARCHIVE = "unarchive"
export const BULK_ACTION_ASSIGN = "assign"
export const BULK_ACTION_REFER = "refer"

export const ARCHIVED_STATUS = "archived"
export const SELECTED_HIDDEN_LABEL = "not visible"

export interface SelectedLeadRef {
  id: string
  status: string
}

export interface BulkLeadResult {
  id: string
  name?: string
  reason?: string
}

export interface BulkLeadResponse {
  error: boolean
  succeeded: BulkLeadResult[]
  failed: BulkLeadResult[]
}

export interface BulkLeadRequest {
  action: string
  lead_ids: string[]
  attorney_id?: string
  firm_id?: string
}

export function toggleSelectedLead(
  selected: readonly SelectedLeadRef[],
  lead: SelectedLeadRef,
): SelectedLeadRef[] {
  if (selected.some((item) => item.id === lead.id)) {
    return selected.filter((item) => item.id !== lead.id)
  }
  return [...selected, { id: lead.id, status: lead.status }]
}

export function hiddenSelectedCount(
  selected: readonly SelectedLeadRef[],
  visibleIds: readonly string[],
): number {
  const visible = new Set(visibleIds)
  return selected.filter((item) => !visible.has(item.id)).length
}

export function partitionArchiveIds(selected: readonly SelectedLeadRef[]): {
  archiveIds: string[]
  unarchiveIds: string[]
} {
  const archiveIds: string[] = []
  const unarchiveIds: string[] = []
  selected.forEach((item) => {
    if (item.status === ARCHIVED_STATUS) {
      unarchiveIds.push(item.id)
      return
    }
    archiveIds.push(item.id)
  })
  return { archiveIds, unarchiveIds }
}

export function chunkIds(ids: readonly string[], size = BULK_BATCH_LIMIT): string[][] {
  const chunks: string[][] = []
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size))
  }
  return chunks
}

export function selectionLabel(selectedCount: number, hiddenCount: number): string {
  if (hiddenCount > 0) {
    return `${selectedCount} selected (${hiddenCount} ${SELECTED_HIDDEN_LABEL})`
  }
  return `${selectedCount} selected`
}

export interface ArchiveConfirmCopy {
  title: string
  description: string
  confirmLabel: string
}

function leadLabel(count: number): string {
  return count === 1 ? "lead" : "leads"
}

export function archiveConfirmCopy(archiveCount: number, unarchiveCount: number): ArchiveConfirmCopy {
  if (archiveCount > 0 && unarchiveCount > 0) {
    const total = archiveCount + unarchiveCount
    return {
      title: `Update ${total} ${leadLabel(total)}?`,
      description: `${archiveCount} will be archived and ${unarchiveCount} already archived will be restored.`,
      confirmLabel: "Update leads",
    }
  }
  if (unarchiveCount > 0) {
    return {
      title: `Restore ${unarchiveCount} ${leadLabel(unarchiveCount)}?`,
      description: "They will leave Archived and go back to their previous status.",
      confirmLabel: unarchiveCount === 1 ? "Restore lead" : "Restore leads",
    }
  }
  return {
    title: `Archive ${archiveCount} ${leadLabel(archiveCount)}?`,
    description: "They will move to Archived. You can restore them later from the Archived filter.",
    confirmLabel: archiveCount === 1 ? "Archive lead" : "Archive leads",
  }
}

export function successToast(actionLabel: string, succeeded: number, total: number): string {
  return `${actionLabel} ${succeeded} of ${total} lead(s).`
}

export async function runChunkedBulk(
  ids: readonly string[],
  send: (chunk: string[]) => Promise<BulkLeadResponse>,
  size = BULK_BATCH_LIMIT,
): Promise<BulkLeadResponse> {
  const succeeded: BulkLeadResult[] = []
  const failed: BulkLeadResult[] = []
  for (const chunk of chunkIds(ids, size)) {
    try {
      const response = await send(chunk)
      succeeded.push(...(response.succeeded ?? []))
      failed.push(...(response.failed ?? []))
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Bulk request failed"
      chunk.forEach((id) => failed.push({ id, reason }))
    }
  }
  return { error: failed.length > 0, succeeded, failed }
}
